import { Box, Stack } from '@mui/material';
import useAgentsChats from './useAgentsChats';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AgentInput from './agent-input/AgentInput';
import { useTimeout } from '../../hooks/useTimeout';
import AgentTypeTag from './AgentTypeTag';
import AgentPrompts from './AgentPrompts';
import { colors, Typography } from '@sweep-io/sweep-design';
import { AI_AGENTS_INFO, CHAT_AREA_MAX_WIDTH } from './aiAgentsConsts';
import AgentIssues from './AgentIssues';
import { PromptGroupsMinimized } from './promptsLibrary/PromptGroupsMinimized';
import { isMetadataTypeAgent } from './helper';
import { useUnhappyStates } from '../pages/environments/useUnhappyStates';
import { AgentsEntryPoint } from '../pages/get-started/agentic-get-started/AgentsEntryPoint';
import { AgentInputMode } from './agent-input/agentInputModeTypes';
import BetaTag from '../common/BetaTag';
import { AgentType } from '@server/ai';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';

interface AgentChatEmptyStateProps {
  title?: string;
  removeSubtitle?: boolean;
  removeAgentTypeTag?: boolean;
  displayPromptsLibrary?: boolean;
  bottomCta?: React.ReactNode;
  maxWidth?: string;
  inputPlaceholder?: string;
  customSubtitle?: React.ReactNode;
  hideIssuesTable?: boolean;
  panelButtons?: ReactNode | null;
  displayAgentsEntryPoint?: boolean;
  setIsParentLoading?: (isLoading: boolean) => void;
}

const AgentChatEmptyState = ({
  title,
  removeSubtitle,
  removeAgentTypeTag = false,
  displayPromptsLibrary,
  bottomCta,
  maxWidth = CHAT_AREA_MAX_WIDTH,
  inputPlaceholder,
  customSubtitle,
  hideIssuesTable = false,
  panelButtons,
  displayAgentsEntryPoint = false,
  setIsParentLoading,
}: AgentChatEmptyStateProps) => {
  const { onConfirmMessage, activeAgent } = useAgentsChats();
  const { setSweepTimeout } = useTimeout();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const { isInitialFetch } = useUnhappyStates();
  const sendBiEvent = useSendBiEvent();

  const [isIssuesCardExpanded, setIsIssuesCardExpanded] = useState(false);
  const isInputAtBottomLayout = isIssuesCardExpanded;

  const [shouldCenter, setShouldCenter] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const underInputRender = useRef<HTMLDivElement>(null);

  const onConfirm = useCallback(
    async (message: string, options?: { advancedMode?: AgentInputMode; additionalOrgIds?: string[] }) => {
      if (!activeAgent) {
        return;
      }
      setIsSubmitting(true);
      await onConfirmMessage({
        messageStr: message,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        replyTo: selectedText || undefined,
        isFirstMessage: true,
        dontSendToServer: isInitialFetch,
        advancedMode: options?.advancedMode,
        additionalOrgIds: options?.additionalOrgIds,
      });
      setIsSubmitting(false);
      setSelectedText(null);
    },
    [activeAgent, isInitialFetch, onConfirmMessage, selectedText],
  );

  const handleTextSelection = useCallback((text: string | null) => {
    setSelectedText(text);
  }, []);

  const onClickPrompt = useCallback(
    async (prompt: string) => {
      if (isSubmitting) {
        return;
      }
      await onConfirm(prompt);
    },
    [isSubmitting, onConfirm],
  );

  const onClickPromptFromLibrary = useCallback(
    async (prompt: string) => {
      sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPromptClick });
      await onClickPrompt(prompt);
    },
    [onClickPrompt, sendBiEvent],
  );

  const onExpandIssuesCard = useCallback((v: boolean) => {
    setIsIssuesCardExpanded(v);
  }, []);

  // Dynamic overflow detection: Determines whether to center content vertically or allow scrolling from top
  // When content fits within container height -> center it
  // When content overflows (like resized AgentIssues table) -> start from top to enable full scrollability
  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && contentRef.current) {
        // For InputAtBottom layout, check overflow in the scrollable content area
        // For normal layout, check overflow in the main container
        const scrollContainer = isInputAtBottomLayout ? contentRef.current : containerRef.current;
        const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;
        setShouldCenter(!hasScroll);
      }
    };

    // Check immediately
    checkOverflow();

    // Check after a brief delay to ensure layout is complete
    setSweepTimeout(checkOverflow, 100);

    // resize can happen upon expanding and/or resizing the issues table
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setSweepTimeout, activeAgent, isInputAtBottomLayout]);

  const { color, name, description, prompts, isSupportIssues } = activeAgent
    ? AI_AGENTS_INFO[activeAgent.type]
    : {
        color: '',
        name: '',
        description: '',
        prompts: { texts: [] },
        isSupportIssues: false,
      };

  const transformedPrompts = useMemo(
    () =>
      prompts?.texts
        ? prompts?.texts?.map((prompt) => ({
            id: prompt,
            name: prompt,
            promptText: prompt,
          }))
        : [],
    [prompts?.texts],
  );

  if (!activeAgent) {
    return null;
  }
  const displayTitle = title || activeAgent.name;

  const headerContent = (
    <Stack gap={2} alignItems="center" justifyContent="center" textAlign="center">
      <Box
        sx={{
          '.MuiTypography-root': {
            fontSize: '36px', // Exception from the design system, approved by UX as a one-off
            lineHeight: 'normal',
            fontWeight: '300',
          },
        }}
      >
        <Typography variant="h1">{displayTitle}</Typography>
      </Box>
      {!removeAgentTypeTag && (
        <Stack direction="row" alignItems="center" gap={1}>
          <AgentTypeTag size="small" color={color} text={name} />
          {activeAgent.type === AgentType.Permissions && <BetaTag />}
        </Stack>
      )}
      {!removeSubtitle && (
        <>
          {customSubtitle || (
            <Typography variant="body" color={colors.grey[700]}>
              {description}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );

  const agentIssues = isSupportIssues && !hideIssuesTable && (
    <AgentIssues
      onExpandIssuesCard={onExpandIssuesCard}
      agent={activeAgent}
      onTextSelection={handleTextSelection}
      tableInitialHeight={350}
    />
  );

  const agentInput = (
    <AgentInput
      onConfirm={onConfirm}
      isLoading={isSubmitting}
      disabled={false}
      selectedText={selectedText}
      onClearSelectedText={() => setSelectedText(null)}
      placeholderText={inputPlaceholder}
    />
  );

  const getPrompts = () => {
    if (displayPromptsLibrary) {
      return (
        <Stack flexWrap="wrap" gap={1}>
          <PromptGroupsMinimized onPromptClick={onClickPromptFromLibrary} />
        </Stack>
      );
    }
    if (prompts?.texts) {
      // Hide prompts for metadata type agents
      if (isMetadataTypeAgent(activeAgent)) {
        return null;
      }

      return (
        <Box alignSelf="flex-start" width="100%">
          <AgentPrompts
            prompts={transformedPrompts}
            onClickPrompt={onClickPrompt}
            isDisabled={isSubmitting}
          />
        </Box>
      );
    }
  };

  return (
    <Stack
      sx={{
        width: '100%',
        alignItems: 'center',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {!!panelButtons && (
        <Box
          sx={{
            position: 'absolute',
            right: '12px',
            top: '12px', //should be vertically aligned with the "agent name" in AgentChat, and in the same place as the buttons in collapsed mode (for smooth transition)
          }}
        >
          {panelButtons}
        </Box>
      )}
      <Stack
        ref={containerRef}
        alignItems="center"
        justifyContent={isInputAtBottomLayout ? undefined : shouldCenter ? 'center' : 'flex-start'}
        overflow="hidden"
        pt={2}
        width="100%"
        flex={1}
      >
        <Stack alignItems="center" height="100%" width="100%" overflow="auto">
          <Stack
            ref={contentRef}
            width="100%"
            p={isInputAtBottomLayout ? '24px 12px 0 12px' : '24px 12px'}
            gap={5}
            maxWidth={maxWidth}
            alignItems="center"
            justifyContent={
              isInputAtBottomLayout ? (shouldCenter ? 'center' : 'flex-start') : 'center'
            }
            flex={1}
          >
            {headerContent}
            {agentIssues}
            {!isInputAtBottomLayout && (
              <>
                <Box width="100%">{agentInput}</Box>
                <Stack
                  ref={underInputRender}
                  alignItems="center"
                  justifyContent="center"
                  gap={'40px'}
                  width="100%"
                >
                  {getPrompts()}
                  {displayAgentsEntryPoint && setIsParentLoading && (
                    <AgentsEntryPoint
                      setIsLoading={setIsParentLoading}
                      popoverAnchorEl={underInputRender.current}
                    />
                  )}
                </Stack>

                {bottomCta && (
                  <Box
                    width="100%"
                    maxWidth={maxWidth}
                    p="12px"
                    display="flex"
                    justifyContent="center"
                  >
                    {bottomCta}
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Stack>

        {isInputAtBottomLayout && (
          <Box width="100%" maxWidth={maxWidth} p="12px">
            {agentInput}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default AgentChatEmptyState;
